import { MantineTextInput } from '@dealstack/frontend/shared/components/mantineForm/MantineTextInput';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Button, Flex, Radio, Text } from '@mantine/core';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import {
	CREATE_PERSON_LEGAL_FORM_VALUES,
	CREATE_PERSON_NATURAL_FORM_VALUES,
	useCreateNewEntityController,
} from './useCreateNewEntityController';
import { PersonType } from '@dealstack/shared/lib/generated';

interface Props {
	className?: string;
	onCreated: (createdEntityId: string) => void;
	onCancel: () => void;
	dealId: string;
}

const CreateNewEntity = ({ dealId, onCreated, onCancel, className }: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		isLegalType,
		type,
		setType,
		createPersonNaturalForm,
		createPersonLegalForm,
		onSubmitNatural,
		onSubmitLegal,
		isLoading,
	} = useCreateNewEntityController(dealId, onCreated);

	return (
		<div className={className}>
			{isLoading && <InlineLoader />}

			<StyledHeader style={{ padding: '1rem' }}>
				<Text size="B6Bold">{tCapManage('entity_select.create_new.title')}</Text>
			</StyledHeader>

			<Flex direction="column" gap="1rem" p="1rem">
				<Radio.Group
					value={type}
					onChange={(value: PersonType) => setType(value)}
					label={
						<Text size="B6Bold" mb="0.5rem">
							{tCapManage('entity_select.create_new.entity_type_label')}
						</Text>
					}
				>
					<Flex gap="1rem">
						<Radio
							value={PersonType.LEGAL}
							label={tCapManage('entity_select.create_new.organisation')}
						/>
						<Radio
							value={PersonType.NATURAL}
							label={tCapManage('entity_select.create_new.natural')}
						/>
					</Flex>
				</Radio.Group>

				{isLegalType ? (
					<FormProvider {...createPersonLegalForm}>
						<MantineTextInput
							key={CREATE_PERSON_LEGAL_FORM_VALUES.NAME}
							size="md"
							label={tCapManage('entity_select.create_new.legal_name')}
							name={CREATE_PERSON_LEGAL_FORM_VALUES.NAME}
						/>
					</FormProvider>
				) : (
					<FormProvider {...createPersonNaturalForm}>
						<MantineTextInput
							key={CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME}
							size="md"
							label={tCapManage('entity_select.create_new.natural_name')}
							name={CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME}
						/>

						<MantineTextInput
							size="md"
							label={tCapManage('entity_select.create_new.natural_last_name')}
							name={CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME}
						/>
					</FormProvider>
				)}

				<Flex gap="1rem">
					<Button
						variant="tertiary"
						onClick={() => {
							void (isLegalType
								? createPersonLegalForm.handleSubmit(onSubmitLegal)()
								: createPersonNaturalForm.handleSubmit(onSubmitNatural)());
						}}
						fullWidth
					>
						{tCapManage('entity_select.create_new.save')}
					</Button>
					<Button variant="default" onClick={onCancel} fullWidth>
						{tCapManage('entity_select.create_new.cancel')}
					</Button>
				</Flex>
			</Flex>
		</div>
	);
};

const StyledHeader = styled.div`
	padding: 1rem;
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
`;

const Memoized = memo(CreateNewEntity);
const Styled = styled(Memoized)`
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 0.5rem;
`;

export { Styled as CreateNewEntity };
